import api from './api';
import { CAB_MEETING_DEFAULT_STATE } from '../constants/cabMeetingOptions';
import type {
  CabMeetingFormData,
  CabMeetingRecord,
  UpdateCabMeetingPayload,
} from '../types/cabMeeting';
import type { CabMeetingState } from '../types/cabMeeting';

function toDateTimeLocalValue(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${y}-${m}-${day}T${hh}:${mm}`;
}

function fromDateTimeLocalValue(value: string): string {
  const d = new Date(value);
  return d.toISOString();
}

export function recordToCabMeetingFormData(record: CabMeetingRecord): CabMeetingFormData {
  const nameParts = record.cabManagerName.trim().split(/\s+/);
  const firstName = nameParts[0] ?? '';
  const lastName = nameParts.slice(1).join(' ');

  const status = (
    ['Pending', 'Scheduled', 'In Progress', 'Completed', 'Cancelled'].includes(record.status)
      ? record.status
      : CAB_MEETING_DEFAULT_STATE
  ) as CabMeetingState;

  return {
    name: record.name,
    status,
    cabManager: record.cabManagerId
      ? {
          id: record.cabManagerId,
          firstName,
          lastName,
          email: '',
        }
      : null,
    delegates: record.delegatesGroupId
      ? { id: record.delegatesGroupId, name: record.delegatesGroupName ?? '' }
      : null,
    boardMembers: record.boardMemberIds.map((memberId, i) => ({
      id: memberId,
      firstName: record.boardMemberNames[i]?.split(/\s+/)[0] ?? '',
      lastName: record.boardMemberNames[i]?.split(/\s+/).slice(1).join(' ') ?? '',
    })),
    boardGroups: record.boardGroupIds.map((groupId, i) => ({
      id: groupId,
      name: record.boardGroupNames[i] ?? groupId,
    })),
    meetingStart: toDateTimeLocalValue(record.scheduledStart),
    meetingEnd: toDateTimeLocalValue(record.scheduledEnd),
    conferenceDetails: record.conferenceDetails ?? '',
    meetingNotes: record.meetingNotes ?? '',
  };
}

export function formDataToUpdateMeetingPayload(
  data: CabMeetingFormData
): UpdateCabMeetingPayload {
  if (!data.cabManager) {
    throw new Error('CAB Manager is required');
  }

  return {
    name: data.name.trim(),
    status: data.status,
    cabManager: data.cabManager,
    delegates: data.delegates,
    boardMembers: data.boardMembers,
    boardGroups: data.boardGroups,
    scheduledStart: fromDateTimeLocalValue(data.meetingStart),
    scheduledEnd: fromDateTimeLocalValue(data.meetingEnd),
    conferenceDetails: data.conferenceDetails || undefined,
    meetingNotes: data.meetingNotes || undefined,
  };
}

export async function getCabMeetingById(meetingId: string): Promise<CabMeetingRecord | null> {
  try {
    const response = await api.get<CabMeetingRecord>(`/cab-meetings/${meetingId}`);
    return response.data;
  } catch (err: unknown) {
    const status = (err as { response?: { status?: number } })?.response?.status;
    if (status === 404) return null;
    throw err;
  }
}

export async function updateCabMeeting(
  meetingId: string,
  payload: UpdateCabMeetingPayload
): Promise<CabMeetingRecord> {
  const response = await api.put<CabMeetingRecord>(`/cab-meetings/${meetingId}`, payload);
  return response.data;
}

export async function deleteCabMeeting(meetingId: string): Promise<void> {
  await api.delete(`/cab-meetings/${meetingId}`);
}

/** Resend/update calendar invitation for all current meeting participants. */
export async function sendNewCabMeetingInvite(meetingId: string): Promise<{
  success: boolean;
  message: string;
  recipientCount: number;
  calendarUpdated: boolean;
  emailsSent: boolean;
}> {
  const response = await api.post<{
    success: boolean;
    message: string;
    recipientCount: number;
    calendarUpdated: boolean;
    emailsSent: boolean;
  }>(`/cab-meetings/${meetingId}/new-meeting-invite`);
  return response.data;
}
